import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  getHasCartLoadFailure,
  getIsSessionInvalid,
  useAppSelector,
} from 'store';
import { useCohesionConfig } from 'utils/eventing';
import { getPriceDetailsFromCart } from 'utils/price';
import { useCart } from 'frontastic';
import { usePayInContext } from 'frontastic/hooks/usePayInContext';
import { usePendingCheckoutAlert } from 'frontastic/hooks/usePendingCheckoutAlert';
import MonarchResolver, { useMonarchData } from '../../monarch/MonarchResolver';
import { CheckoutResult } from '../../monarch/rules/checkout/types';
import { ValueReinforcementResult } from '../../monarch/rules/valueReinforcement/types';
import { ValueReinforcement } from '../../value-reinforcement';
import { CheckoutSkeleton } from '../skeletons';
import { CheckoutFormSkeleton } from '../skeletons/checkout-form';
import { CheckoutProps } from '../types';
import { FreeOrderCheckoutForm } from './checkout-form/free-order-form';
import {
  Alert,
  CheckoutForm,
  EmptyCartCard,
  OrderDetails,
  OrderSummary,
  PaymentMethodSelector,
  ProductLineItem,
  SessionInvalidAlertModal,
  StripeFormProvider,
} from '.';

export const CheckoutControl = ({ stripePublishableKey }: CheckoutProps) => {
  usePendingCheckoutAlert();

  const hasCartLoadFailure = useAppSelector(getHasCartLoadFailure);
  const isSessionInvalid = useAppSelector(getIsSessionInvalid);
  const { data, isEmpty, isLoading, redeemDiscountCode, removeDiscountCode } =
    useCart();

  const lineItems = data?.lineItems ?? [];
  const priceDetails = getPriceDetailsFromCart(data);
  const isCreditPurchase = lineItems.some(
    (item) => item.variant.attributes?.mode === 'credit',
  );

  const isEmptyCart = isEmpty || lineItems.length === 0;
  const shouldHideAlert = isSessionInvalid && !hasCartLoadFailure;
  const purchaseType = lineItems.some(({ bundleId }) => bundleId)
    ? 'program'
    : lineItems[0]?.variant?.attributes?.mode;
  const isFreeOrder = priceDetails.cartTotal === 0;
  const monarchCheckout = useMonarchData<CheckoutResult>(
    'checkout',
  ) as CheckoutResult | null;
  const shouldShowUpi = monarchCheckout?.data?.showUpi ?? false;

  // Empty until AddressForm reports a selection; UPI stays hidden until then.
  const [billingCountry, setBillingCountry] = useState('');

  // This flow always settles in the cart currency, so a failed pay-in context
  // falls straight back to it rather than surfacing a currency alert.
  const {
    stripeOptions,
    elementsKey,
    isLoading: isPayInContextLoading,
  } = usePayInContext({
    cartId: data?.cartId,
    showUpi: shouldShowUpi,
    billingCountry,
    enabled: Boolean(data?.cartId) && !isFreeOrder,
    fallback: {
      amount: priceDetails.cartTotal,
      currency: priceDetails.currencyCode,
    },
  });

  useCohesionConfig('checkout');

  const renderCheckout = () => (
    <div className="row">
      <section className="col-md-5 pr-md-5 col-cart-summary">
        <h1 className="sr-only">
          <FormattedMessage
            id="payment.checkout.main.heading"
            defaultMessage="Payment"
            description="Heading For Checkout for Screen Readers"
          />
        </h1>
        <div>
          <h2 className="h5">
            <FormattedMessage
              id="payment.product.line.item.purchase.cart.heading"
              defaultMessage="In Your Cart"
              description="Heading of the cart in product details section"
            />
          </h2>
          <p>
            <FormattedMessage
              id="payment.product.line.item.purchase.cart.subheading"
              defaultMessage="Your purchase contains the following:"
              description="Subheading of the cart in product details section"
            />
          </p>
          <div className="list-wrapper">
            {lineItems.map((item) => (
              <ProductLineItem key={item.lineItemId} item={item} />
            ))}
          </div>
        </div>
        <OrderSummary
          isCreditPurchase={isCreditPurchase}
          priceDetails={priceDetails}
          onApplyCoupon={redeemDiscountCode(data!.cartId!)}
          onRemoveCoupon={removeDiscountCode(data!.cartId!)}
        />
        <OrderDetails purchaseType={purchaseType} />
      </section>

      <section className="col-md-7 pl-md-5">
        {!isFreeOrder && <PaymentMethodSelector />}
        {!isFreeOrder && stripeOptions && (
          <StripeFormProvider
            key={elementsKey ?? undefined}
            stripePublishableKey={stripePublishableKey}
            options={stripeOptions}
          >
            <CheckoutForm
              cartID={data!.cartId!}
              priceDetails={priceDetails}
              lineItems={lineItems}
              showUpi={shouldShowUpi}
              onBillingCountryChange={setBillingCountry}
            />
          </StripeFormProvider>
        )}
        {!isFreeOrder && !stripeOptions && isPayInContextLoading && (
          <CheckoutFormSkeleton />
        )}
        {isFreeOrder && (
          <FreeOrderCheckoutForm
            cartID={data!.cartId!}
            priceDetails={priceDetails}
            lineItems={lineItems}
          />
        )}
        <MonarchResolver<ValueReinforcementResult>
          experimentKey="useValueReinforcement"
          variants={{
            control: null,
            test: <ValueReinforcement />,
          }}
          fallbackVariant="control"
          Loader={<div>Loading...</div>}
        />
      </section>
    </div>
  );

  if (isLoading) {
    return <CheckoutSkeleton />;
  }

  return (
    <div className="container-fluid py-5">
      <SessionInvalidAlertModal isOpen={isSessionInvalid} />
      {shouldHideAlert ? null : <Alert />}
      {hasCartLoadFailure ? null : isEmptyCart ? (
        <EmptyCartCard />
      ) : (
        renderCheckout()
      )}
    </div>
  );
};
